import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import FullScreenPanel from "../ui/FullScreenPanel.jsx";
import { morePanelGroups } from "../pages";
import { useMobileMode } from "../MobileModeContext.jsx";
import { AdminContext, requestAdminPin } from "../../adminSession.jsx";
import { SiteAuthContext } from "../../siteAuth";
import { useThemeMode } from "../../ThemeContext";
import { phoneConfirm } from "../ui/dialogs.jsx";
import { currentBuildName } from "./buildInfo";

function untilText(ms) {
  if (!ms) return "";
  const d = new Date(ms);
  // up to 30 days away: the day too ("Nov 19, 3:42 PM")
  return `${d.toLocaleDateString([], { month: "short", day: "numeric" })}, ${d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`;
}

/**
 * Everything the bottom tabs don't hold: every in-scope page (plan "Pages on the phone"), the
 * admin unlock, this device's settings and sign-out. A page with no phone screen yet opens
 * today's page ("desktop view"); an admin page asks for the PIN as it does today.
 */
export default function MorePanel({ open, onClose }) {
  const navigate = useNavigate();
  const mode = useMobileMode();
  const admin = useContext(AdminContext);
  const site = useContext(SiteAuthContext);
  const theme = useThemeMode();
  const [busy, setBusy] = useState(false);

  const isAdmin = Boolean(admin && admin.isAdminActive);
  const groups = morePanelGroups(mode.features);

  const go = (path) => {
    onClose();
    navigate(path);
  };

  const unlock = async () => {
    setBusy(true);
    try {
      const user = await requestAdminPin({ reason: admin ? admin.rememberNote : null });
      if (user && admin) admin.loginAsAdmin(user, { remember: user.remember });
    } finally {
      setBusy(false);
    }
  };

  const useDesktop = async () => {
    const ok = await phoneConfirm(
      "This phone will show the desktop pages, as before the phone app. To come back: open the menu (☰) at the top and tap \"Use phone layout\".",
      { title: "Use desktop layout", confirmLabel: "Use desktop layout" }
    );
    if (ok) mode.setForceDesktop(true);
  };

  const signOut = async () => {
    const ok = await phoneConfirm("Sign out of JobQ on this phone? You'll need the site password to sign back in.", {
      title: "Sign out", confirmLabel: "Sign out", danger: true,
    });
    if (ok && site && site.handleLogout) {
      onClose();
      site.handleLogout();
    }
  };

  return (
    <FullScreenPanel open={open} title="More" onClose={onClose} testId="more-panel">
      <section className="jq-more-section" aria-label="Admin">
        {isAdmin ? (
          <div className="jq-admin-card">
            <div>
              <div className="jq-admin-name"><i className="fas fa-unlock me-2" aria-hidden="true" />Admin: {admin.adminData.userName}</div>
              <div className="jq-muted">
                {admin.rememberedUntil
                  ? `Remembered on this phone until ${untilText(admin.rememberedUntil)}`
                  : "Until this app is closed"}
              </div>
              {admin.rememberNote && <div className="jq-muted small" role="status">{admin.rememberNote}</div>}
            </div>
            <button type="button" className="btn btn-outline-danger" onClick={admin.logoutAdmin}>
              Clear Credentials
            </button>
          </div>
        ) : (
          <button type="button" className="btn btn-outline-primary btn-lg w-100" onClick={unlock} disabled={busy || Boolean(admin && admin.restoring)}>
            <i className="fas fa-lock me-2" aria-hidden="true" />{busy ? "Checking…" : admin && admin.restoring ? "Unlocking…" : "Unlock admin"}
          </button>
        )}
        {!isAdmin && admin && admin.rememberNote && (
          <div className="jq-muted small mt-2" role="status">{admin.rememberNote}</div>
        )}
      </section>

      {groups.map((g) => (
        <section key={g.title} className="jq-more-section" aria-label={g.title}>
          <h3 className="jq-more-heading">{g.title}</h3>
          <ul className="jq-more-list">
            {g.pages.map((p) => {
              const phoneScreen = mode.activePages.has(p.key);
              return (
                <li key={p.key}>
                  <button type="button" className="jq-more-item" onClick={() => go(p.path)}>
                    <i className={`fas ${p.icon} jq-more-icon`} aria-hidden="true" />
                    <span className="jq-more-label">{p.label}</span>
                    {!phoneScreen && <span className="jq-tag">desktop view</span>}
                    {p.admin && !isAdmin && <i className="fas fa-lock jq-muted" aria-label="needs the admin PIN" />}
                    <i className="fas fa-chevron-right jq-muted" aria-hidden="true" />
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      ))}

      <section className="jq-more-section" aria-label="This phone">
        <h3 className="jq-more-heading">This phone</h3>
        <ul className="jq-more-list">
          <li>
            <button type="button" className="jq-more-item" onClick={() => theme && theme.toggleMode()}>
              <i className={`fas ${theme && theme.isDark ? "fa-sun" : "fa-moon"} jq-more-icon`} aria-hidden="true" />
              <span className="jq-more-label">{theme && theme.isDark ? "Light mode" : "Dark mode"}</span>
            </button>
          </li>
          <li>
            <button type="button" className="jq-more-item" onClick={useDesktop}>
              <i className="fas fa-desktop jq-more-icon" aria-hidden="true" />
              <span className="jq-more-label">Use desktop layout on this phone</span>
            </button>
          </li>
          {(mode.prefs.preview || isAdmin) && (
            <li>
              <label className="jq-more-item jq-more-switch form-switch">
                <i className="fas fa-flask jq-more-icon" aria-hidden="true" />
                <span className="jq-more-label">
                  Phone preview
                  <span className="jq-muted d-block">Phone screens that aren't switched on for everyone yet</span>
                </span>
                <input
                  type="checkbox"
                  className="form-check-input"
                  role="switch"
                  checked={mode.prefs.preview}
                  onChange={(e) => mode.setPreview(e.target.checked)}
                />
              </label>
            </li>
          )}
          <li>
            <button type="button" className="jq-more-item" onClick={signOut}>
              <i className="fas fa-sign-out-alt jq-more-icon" aria-hidden="true" />
              <span className="jq-more-label">Sign out</span>
            </button>
          </li>
        </ul>
        <div className="jq-muted jq-build">Build {currentBuildName() || "dev"}</div>
      </section>
    </FullScreenPanel>
  );
}
